/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import Player from 'video.js/dist/types/player';
import type { TVideoPlayerProps } from './model';
import 'jb-videojs-hls-quality-selector';
import videojsQualityLevels from 'videojs-contrib-quality-levels';
import './video.css';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { FsIconButton } from '@fs/core';

videojs.registerPlugin('qualityLevels', videojsQualityLevels);

export default function VideoPlayer({
  onReady,
  options,
  hasMuteable = false,
  onError,
}: Readonly<
  TVideoPlayerProps & {
    hasMuteable?: boolean;
    onError?: Function;
  }
>) {
  const videoRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<Player | null>(null);
  const [isMuted, setIsMuted] = useState(hasMuteable || false);

  useEffect(() => {
    if (!playerRef.current) {
      const videoElement = document.createElement('video-js');
      videoElement.classList.add('vjs-big-play-centered');
      videoRef.current?.appendChild(videoElement);
      videoElement.style.borderRadius = '16px';

      const player = (playerRef.current = videojs(
        videoElement,
        { ...options },
        () => {
          videojs.log('player is ready');
          if (onReady) onReady(player);
        }
      ));

      if (options.sources && options.sources.length > 0) {
        player.src(options.sources[0]);
      }

      if ((player as any).qualityLevels) {
        (player as any).qualityLevels();
      }
      player.on('error', () => {
        const error = player.error();
        console.log('Video.js Error:', error);
        onError && onError(error);

        // Handle different error types
        switch (error?.code) {
          case 1: // MEDIA_ERR_ABORTED
            console.log('Video loading was aborted');
            break;
          case 2: // MEDIA_ERR_NETWORK
            console.log('Network error occurred');
            break;
          case 3: // MEDIA_ERR_DECODE
            console.log('Video decoding error');
            break;
          case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
            console.log('Video format not supported');
            break;
          default:
            console.log('Unknown error occurred');
        }
      });
      player.on('ready', () => {
        (player as any).hlsQualitySelector({
          displayCurrentQuality: true,
        });
        const playButton = document.querySelector('.vjs-big-play-button');
        if (playButton) {
          playButton.classList.add('custom-play-button');
        }
      });

      // Add volume change listener to sync mute state
      player.on('volumechange', () => {
        setIsMuted(player.muted()!);
      });
    }
  }, [onReady, options]);

  useEffect(() => {
    const player = playerRef.current;
    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  const toggleMute = () => {
    if (playerRef.current) {
      const player = playerRef.current;
      player.muted(!player.muted());
      setIsMuted(player.muted()!);
    }
  };

  return (
    <div data-vjs-player style={{ position: 'relative' }}>
      <div ref={videoRef} />
      {hasMuteable && (
        <FsIconButton
          onClick={(e) => {
            e.stopPropagation();
            toggleMute();
          }}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 10,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 1)',
            },
            boxShadow: 6,
          }}
          size="medium"
        >
          {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
        </FsIconButton>
      )}
    </div>
  );
}
